/**
 * On Behalf Of Tab
 * Spec: docs/enter-time-for-others-spec.md §3 Org-Level Configuration
 *
 * State is lifted into useSettingsFormState — saved via the shared
 * "Save Changes" footer bar in SettingsPage, not a local save button.
 *
 * Types and mock data live in ./onBehalfOfTypes.ts so this file
 * stays a pure React module and Vite Fast Refresh works correctly.
 */

import React, { useState, useRef, useMemo, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Users, Trash2, Plus, Upload, Download, Check, ChevronsUpDown, AlertCircle,
} from 'lucide-react';
import { TabsContent, Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { DataTableEnhanced } from '@/components/ui/data-table-enhanced';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '@/components/ui/command';
import { MultiSelect } from '@/components/ui/multiselect';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import type { OnBehalfOfSettings, Relationship, Scope } from './onBehalfOfTypes';
import type { OnBehalfOfPeople } from '@/services/settingsRepository';
import { TabAccessAlerts } from './TabAccessAlerts';

// ─── Constants ───────────────────────────────────────────────────────────────

const SCOPE_OPTIONS: { value: Scope; label: string; description: string }[] = [
  {
    value: 'own-department',
    label: 'Own Department',
    description: 'Authorised users can act for anyone in their own department.',
  },
  {
    value: 'specific-users',
    label: 'Specific Users',
    description: 'Authorised users can only act for specifically named individuals.',
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

// Ids must stay unique across reloads now that relationships are persisted
function generateId() {
  return globalThis.crypto?.randomUUID?.() ?? `r${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const relationshipKey = (person: string, actingFor: string) => `${person}\u0000${actingFor}`;

function parseExcel(buffer: ArrayBuffer): { person: string; actingFor: string }[] {
  const wb = XLSX.read(buffer, { type: 'array' });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '' });
  return rows.map(row => ({
    person: (row['Person'] ?? '').trim(),
    actingFor: (row['Acting For'] ?? '').trim(),
  }));
}

function exportToExcel(rows: Relationship[], filename: string) {
  const data = [['Person', 'Acting For'], ...rows.map(r => [r.person, r.actingFor])];
  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Relationships');
  XLSX.writeFile(wb, filename);
}

// ─── ScopeCard ───────────────────────────────────────────────────────────────

interface ScopeCardProps {
  id: string; label: string; description: string; selected: boolean; onSelect: () => void;
}

function ScopeCard({ id, label, description, selected, onSelect }: ScopeCardProps) {
  return (
    <div
      role="radio"
      aria-checked={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect()}
      className={cn(
        'flex items-center justify-between gap-4 p-4 rounded-lg border bg-background cursor-pointer transition-colors',
        'hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        selected && 'border-primary/60',
      )}
    >
      <div className="flex-1 min-w-0">
        <Label htmlFor={id} className="text-sm font-medium cursor-pointer pointer-events-none">{label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <span
        id={id}
        aria-hidden="true"
        className={cn(
          'h-4 w-4 shrink-0 rounded-full border flex items-center justify-center transition-colors',
          selected ? 'border-primary bg-primary' : 'border-primary bg-transparent',
        )}
      >
        {selected && <span className="block h-1.5 w-1.5 rounded-full bg-background" />}
      </span>
    </div>
  );
}

// ─── ScopeGroup ──────────────────────────────────────────────────────────────

interface ScopeGroupProps { prefix: string; value: Scope; onChange: (v: Scope) => void; }

function ScopeGroup({ prefix, value, onChange }: ScopeGroupProps) {
  return (
    <div className="ml-1 space-y-2">
      <div className="px-1 pt-1 pb-0.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Scope</span>
      </div>
      <div role="radiogroup" aria-label="Scope" className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {SCOPE_OPTIONS.map((opt) => (
          <ScopeCard
            key={`${prefix}-${opt.value}`}
            id={`${prefix}-${opt.value}`}
            label={opt.label}
            description={opt.description}
            selected={value === opt.value}
            onSelect={() => onChange(opt.value)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── ToggleRow ───────────────────────────────────────────────────────────────

interface ToggleRowProps {
  id: string; label: string; subLabel: string;
  checked: boolean; onCheckedChange: (v: boolean) => void;
}

function ToggleRow({ id, label, subLabel, checked, onCheckedChange }: ToggleRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
      <div className="flex-1 min-w-0">
        <Label htmlFor={id} className="text-sm font-medium cursor-pointer">{label}</Label>
        <p className="text-xs text-muted-foreground mt-0.5">{subLabel}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

// ─── PersonCombobox — single-select searchable ────────────────────────────────

interface PersonComboboxProps {
  value: string; onChange: (v: string) => void;
  options: string[]; placeholder?: string;
}

function PersonCombobox({ value, onChange, options, placeholder = 'Select person...' }: PersonComboboxProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          {value || <span className="text-muted-foreground">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search..." />
          <CommandList>
            <CommandEmpty>No person found.</CommandEmpty>
            <CommandGroup>
              {options.map((name) => (
                <CommandItem key={name} value={name} onSelect={() => { onChange(name); setOpen(false); }}>
                  <Check className={cn('mr-2 h-4 w-4', value === name ? 'opacity-100' : 'opacity-0')} />
                  {name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// ─── AddRelationshipModal ─────────────────────────────────────────────────────

interface AddRelationshipModalProps {
  open: boolean; onClose: () => void;
  onAdd: (person: string, actingFor: string[]) => void;
  people: string[];
  actingForCandidates: string[];
}

function AddRelationshipModal({ open, onClose, onAdd, people, actingForCandidates }: AddRelationshipModalProps) {
  const [person, setPerson] = useState('');
  const [actingFor, setActingFor] = useState<string[]>([]);

  const reset = () => { setPerson(''); setActingFor([]); };
  const handleClose = () => { reset(); onClose(); };
  const handleAdd = () => {
    if (!person || actingFor.length === 0) return;
    onAdd(person, actingFor);
    reset(); onClose();
  };

  const actingForOptions = useMemo(
    () => actingForCandidates.filter(p => p !== person).map(p => ({ value: p, label: p })),
    [actingForCandidates, person],
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add Relationship</DialogTitle>
          <DialogDescription>
            Select who will be acting and who they can act for. Multiple "Acting For" people can be selected.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Person</Label>
            <PersonCombobox
              value={person}
              onChange={(val) => { setPerson(val); setActingFor([]); }}
              options={people}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Acting For</Label>
            <MultiSelect
              options={actingForOptions}
              value={actingFor}
              onValueChange={setActingFor}
              placeholder="Select one or more people..."
              showSearch
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleAdd} disabled={!person || actingFor.length === 0}>Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── ImportExcelModal ─────────────────────────────────────────────────────────

interface ImportPreviewRow {
  rowNum: number; person: string; actingFor: string; error?: string;
}

interface ImportExcelModalProps {
  open: boolean; onClose: () => void;
  onConfirm: (rows: { person: string; actingFor: string }[]) => void;
  people: string[];
  actingForCandidates: string[];
}

function ImportExcelModal({ open, onClose, onConfirm, people, actingForCandidates }: ImportExcelModalProps) {
  const [preview, setPreview] = useState<ImportPreviewRow[]>([]);
  const [fileName, setFileName] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const peopleByName = useMemo(
    () => new Map(people.map(p => [p.toLowerCase(), p])),
    [people],
  );
  const actingForByName = useMemo(
    () => new Map(actingForCandidates.map(p => [p.toLowerCase(), p])),
    [actingForCandidates],
  );

  const reset = () => { setPreview([]); setFileName(''); if (fileRef.current) fileRef.current.value = ''; };
  const handleClose = () => { reset(); onClose(); };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseExcel(ev.target?.result as ArrayBuffer);
        const rows: ImportPreviewRow[] = parsed.map((row, i) => {
          const rowNum = i + 2;
          let error: string | undefined;
          const person = peopleByName.get(row.person.trim().toLowerCase());
          const actingFor = actingForByName.get(row.actingFor.trim().toLowerCase());
          if (!row.person) error = `Row ${rowNum}: Person name is empty`;
          else if (!person) error = `Row ${rowNum}: '${row.person}' not found`;
          else if (!row.actingFor) error = `Row ${rowNum}: Acting For name is empty`;
          else if (!actingFor) error = `Row ${rowNum}: '${row.actingFor}' not found`;
          return { rowNum, person: person ?? row.person, actingFor: actingFor ?? row.actingFor, error };
        });
        setPreview(rows);
      } catch {
        reset();
        toast.error('Could not read the file. Make sure it is a valid .xlsx file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const validRows = preview.filter(r => !r.error);

  const handleConfirm = () => {
    onConfirm(validRows.map(({ person, actingFor }) => ({ person, actingFor })));
    toast.success(`${validRows.length} relationship${validRows.length !== 1 ? 's' : ''} imported`);
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>Import Excel</DialogTitle>
          <DialogDescription>
            Upload an Excel file (.xlsx) with two columns: <strong>Person</strong> and <strong>Acting For</strong>.
            New rows are added; existing rows updated; nothing is deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
              <Upload className="h-3.5 w-3.5 mr-1.5" />
              Choose file
            </Button>
            <span className="text-sm text-muted-foreground">{fileName || 'No file selected'}</span>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
          </div>

          {preview.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Preview — {validRows.length} valid / {preview.length} total
              </p>
              <div className="rounded-lg border overflow-hidden max-h-56 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Row</TableHead>
                      <TableHead>Person</TableHead>
                      <TableHead>Acting For</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row) => (
                      <TableRow key={row.rowNum} className={row.error ? 'bg-destructive/5' : ''}>
                        <TableCell className="text-muted-foreground text-xs">{row.rowNum}</TableCell>
                        <TableCell>{row.person || <span className="text-muted-foreground italic">empty</span>}</TableCell>
                        <TableCell>{row.actingFor || <span className="text-muted-foreground italic">empty</span>}</TableCell>
                        <TableCell>
                          {row.error ? (
                            <span className="flex items-center gap-1 text-xs text-destructive">
                              <AlertCircle className="h-3 w-3 shrink-0" />{row.error}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-xs text-emerald-600">
                              <Check className="h-3 w-3 shrink-0" />Valid
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {preview.some(r => r.error) && (
                <p className="text-xs text-muted-foreground">
                  Rows with errors will be skipped. Only valid rows are imported.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={validRows.length === 0}>
            Confirm Import ({validRows.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── RelationshipTable ────────────────────────────────────────────────────────

interface RelationshipTableProps {
  relationships: Relationship[];
  onRemove: (id: string) => void;
  onRemoveMany: (ids: string[]) => void;
}

function RelationshipTable({ relationships, onRemove, onRemoveMany }: RelationshipTableProps) {
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [pendingBulkRemove, setPendingBulkRemove] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Relationship[]>([]);
  const [clearSelection, setClearSelection] = useState(false);

  // Row selection is index-based, so reset it whenever the underlying rows change
  useEffect(() => {
    setSelectedRows([]);
    setClearSelection(true);
  }, [relationships]);

  useEffect(() => {
    if (!clearSelection) return;
    const timer = setTimeout(() => setClearSelection(false), 0);
    return () => clearTimeout(timer);
  }, [clearSelection]);

  // Pre-sort: Person A→Z, then Acting For A→Z — keeps all rows for the same
  // person together in the default view. Users can re-sort via column headers.
  const sortedData = useMemo(
    () =>
      [...relationships].sort(
        (a, b) =>
          a.person.localeCompare(b.person) ||
          a.actingFor.localeCompare(b.actingFor),
      ),
    [relationships],
  );

  const columns: ColumnDef<Relationship>[] = useMemo(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value: boolean | 'indeterminate') => table.toggleAllPageRowsSelected(!!value)}
            aria-label="Select all"
            className="translate-y-[2px]"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value: boolean | 'indeterminate') => row.toggleSelected(!!value)}
            aria-label={`Select: ${row.original.person} acts for ${row.original.actingFor}`}
            className="translate-y-[2px]"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        ),
        enableSorting: false,
        enableGrouping: false,
        enableHiding: false,
      },
      {
        accessorKey: 'person',
        header: 'Person',
        cell: ({ row }) => (
          <span className="font-medium text-sm">{row.original.person}</span>
        ),
        enableSorting: true,
        enableGrouping: false,
      },
      {
        accessorKey: 'actingFor',
        header: 'Acting For',
        cell: ({ row }) => (
          <span className="text-sm">{row.original.actingFor}</span>
        ),
        enableSorting: true,
        enableGrouping: false,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                setPendingRemoveId(row.original.id);
              }}
              aria-label={`Remove: ${row.original.person} acts for ${row.original.actingFor}`}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ),
        enableSorting: false,
        enableGrouping: false,
        enableHiding: false,
      },
    ],
    [],
  );

  if (relationships.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        No relationships configured. Use 'Add' to set up who can act for who.
      </div>
    );
  }

  return (
    <>
      <DataTableEnhanced
        columns={columns}
        data={sortedData}
        enableSorting={true}
        enableGrouping={false}
        enableColumnVisibility={false}
        enableFiltering={true}
        searchKey="person"
        searchPlaceholder="Search by person or acting for..."
        enableRowSelection={true}
        onSelectionChange={setSelectedRows}
        clearSelection={clearSelection}
        hideFieldSelector={true}
        pageSize={20}
        hidePagination={relationships.length <= 20}
        toolbarActions={
          selectedRows.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setPendingBulkRemove(true)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete Selected ({selectedRows.length})
            </Button>
          )
        }
      />

      <AlertDialog
        open={pendingRemoveId !== null || pendingBulkRemove}
        onOpenChange={(v) => {
          if (!v) {
            setPendingRemoveId(null);
            setPendingBulkRemove(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingBulkRemove && selectedRows.length > 1
                ? `Remove ${selectedRows.length} relationships?`
                : 'Remove this relationship?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingBulkRemove && selectedRows.length > 1
                ? 'The selected acting relationships will be removed. This action cannot be undone.'
                : 'This acting relationship will be removed. This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingBulkRemove) {
                  onRemoveMany(selectedRows.map(r => r.id));
                  setPendingBulkRemove(false);
                } else if (pendingRemoveId) {
                  onRemove(pendingRemoveId);
                  setPendingRemoveId(null);
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── RelationshipManager ──────────────────────────────────────────────────────

interface RelationshipManagerProps {
  entryRelationships: Relationship[];
  approvalRelationships: Relationship[];
  onEntryChange: (rows: Relationship[]) => void;
  onApprovalChange: (rows: Relationship[]) => void;
  defaultTab: 'entry' | 'approval';
  showEntry: boolean;
  showApproval: boolean;
  people: OnBehalfOfPeople;
}

function RelationshipManager({
  entryRelationships,
  approvalRelationships,
  onEntryChange,
  onApprovalChange,
  defaultTab,
  showEntry,
  showApproval,
  people,
}: RelationshipManagerProps) {
  const [activeTab, setActiveTab] = useState<'entry' | 'approval'>(defaultTab);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  const isEntry = activeTab === 'entry';
  const currentRows = isEntry ? entryRelationships : approvalRelationships;
  const onCurrentChange = isEntry ? onEntryChange : onApprovalChange;
  const entryActingFor = useMemo(
    () => [...people.people, ...people.contractors],
    [people],
  );
  const actingForCandidates = isEntry ? entryActingFor : people.people;

  const handleAdd = (person: string, actingFor: string[]) => {
    const newRows = actingFor
      .filter(af => !currentRows.some(r => r.person === person && r.actingFor === af))
      .map(af => ({ id: generateId(), person, actingFor: af }));
    onCurrentChange([...currentRows, ...newRows]);
  };

  const handleRemove = (id: string) => {
    onCurrentChange(currentRows.filter(r => r.id !== id));
  };

  const handleRemoveMany = (ids: string[]) => {
    onCurrentChange(currentRows.filter(r => !ids.includes(r.id)));
  };

  const handleImport = (incoming: { person: string; actingFor: string }[]) => {
    const updated = [...currentRows];
    const seen = new Set(currentRows.map(r => relationshipKey(r.person, r.actingFor)));
    incoming.forEach(({ person, actingFor }) => {
      const key = relationshipKey(person, actingFor);
      if (seen.has(key)) return;
      seen.add(key);
      updated.push({ id: generateId(), person, actingFor });
    });
    onCurrentChange(updated);
  };

  const handleExport = () => {
    try {
      exportToExcel(
        currentRows,
        isEntry ? 'enter-time-for-others.xlsx' : 'approve-on-behalf-of-others.xlsx',
      );
      toast.success('Excel file exported');
    } catch {
      toast.error('Export failed. Please try again.');
    }
  };

  return (
    <div className="space-y-3 pt-2">
      <div className="px-1 pb-0.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Manage Relationships
        </span>
      </div>

      <div className="rounded-lg border bg-background overflow-hidden">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'entry' | 'approval')}>

          {/* Tab strip + action buttons */}
          <div className="flex items-center justify-between gap-2 border-b px-4 pt-3">

            {/* Only show the tab strip when both are visible */}
            {showEntry && showApproval ? (
              <TabsList className="h-9 bg-transparent p-0 gap-0 -mb-px">
                <TabsTrigger
                  value="entry"
                  className={cn(
                    'rounded-none border-b-2 px-3 pb-2.5 text-sm font-medium transition-colors',
                    'border-transparent text-muted-foreground',
                    'data-[state=active]:border-primary data-[state=active]:text-foreground',
                    'data-[state=active]:bg-transparent data-[state=active]:shadow-none',
                    'hover:text-foreground',
                  )}
                >
                  Enter Time for Others
                </TabsTrigger>
                <TabsTrigger
                  value="approval"
                  className={cn(
                    'rounded-none border-b-2 px-3 pb-2.5 text-sm font-medium transition-colors',
                    'border-transparent text-muted-foreground',
                    'data-[state=active]:border-primary data-[state=active]:text-foreground',
                    'data-[state=active]:bg-transparent data-[state=active]:shadow-none',
                    'hover:text-foreground',
                  )}
                >
                  Approve on Behalf of Others
                </TabsTrigger>
              </TabsList>
            ) : (
              /* Single-tab mode — just show a plain heading */
              <p className="text-sm font-medium pb-2.5 text-foreground">
                {showEntry ? 'Enter Time for Others' : 'Approve on Behalf of Others'}
              </p>
            )}

            <div className="flex items-center gap-2 pb-2 shrink-0">
              <Button variant="outline" size="sm" onClick={() => setImportOpen(true)}>
                <Upload className="h-3.5 w-3.5 mr-1.5" />
                Import Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={currentRows.length === 0}
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                Export Excel
              </Button>
              <Button size="sm" onClick={() => setAddOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                Add
              </Button>
            </div>
          </div>

          {/* Tab panels — only render the enabled ones */}
          <div className="p-4">
            {showEntry && (
              <TabsContent value="entry" className="mt-0">
                <RelationshipTable
                  relationships={entryRelationships}
                  onRemove={handleRemove}
                  onRemoveMany={handleRemoveMany}
                />
              </TabsContent>
            )}
            {showApproval && (
              <TabsContent value="approval" className="mt-0">
                <RelationshipTable
                  relationships={approvalRelationships}
                  onRemove={handleRemove}
                  onRemoveMany={handleRemoveMany}
                />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>

      <AddRelationshipModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={handleAdd}
        people={people.people}
        actingForCandidates={actingForCandidates}
      />
      <ImportExcelModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onConfirm={handleImport}
        people={people.people}
        actingForCandidates={actingForCandidates}
      />
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface OnBehalfOfTabProps {
  settings: OnBehalfOfSettings;
  onChange: (updates: Partial<OnBehalfOfSettings>) => void;
  /** Names that can be used in relationships (mock list in demo, org employees in live) */
  people: OnBehalfOfPeople;
  hasChanges: boolean;
  canEdit: boolean;
}

// ─── Tab (default export) ─────────────────────────────────────────────────────

export default function OnBehalfOfTab({ settings, onChange, people, hasChanges, canEdit }: OnBehalfOfTabProps) {
  const enterSpecific = settings.enterTimeForOthersEnabled
    && settings.enterTimeForOthersScope === 'specific-users';
  const approveSpecific = settings.approveOnBehalfOfEnabled
    && settings.approveOnBehalfOfScope === 'specific-users';
  const showManager = enterSpecific || approveSpecific;
  const defaultTab: 'entry' | 'approval' = enterSpecific ? 'entry' : 'approval';

  return (
    <TabsContent value="on-behalf-of" className="space-y-6">
      <TabAccessAlerts
        canEdit={canEdit}
        hasChanges={hasChanges}
        viewOnlyText="You have view-only access to On Behalf Of. Contact your administrator to request edit access."
        unsavedText={'You have unsaved changes. Click "Save Settings" at the bottom to apply your changes.'}
      />

      <div className="rounded-lg border bg-card shadow-sm">

        {/* Section header */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <Users className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-sm">Enter Time for Others</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Allow authorised users to enter timesheets and approve on behalf of others.
            </p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-6">

          {/* Toggle 1 + scope */}
          <div className="space-y-4">
            <ToggleRow
              id="enterTimeForOthersEnabled"
              label="Enable Enter Time for Others"
              subLabel="Allows authorised users to enter and submit timesheets on behalf of another person."
              checked={settings.enterTimeForOthersEnabled}
              onCheckedChange={(val) => onChange({ enterTimeForOthersEnabled: val })}
            />
            {settings.enterTimeForOthersEnabled && (
              <ScopeGroup
                prefix="enter"
                value={settings.enterTimeForOthersScope}
                onChange={(val) => onChange({ enterTimeForOthersScope: val })}
              />
            )}
          </div>

          {/* Toggle 2 + scope */}
          <div className="space-y-4">
            <ToggleRow
              id="approveOnBehalfOfEnabled"
              label="Enable Approve on Behalf of Others"
              subLabel="Allows authorised users to approve timesheets sitting in another approver's queue."
              checked={settings.approveOnBehalfOfEnabled}
              onCheckedChange={(val) => onChange({ approveOnBehalfOfEnabled: val })}
            />
            {settings.approveOnBehalfOfEnabled && (
              <ScopeGroup
                prefix="approve"
                value={settings.approveOnBehalfOfScope}
                onChange={(val) => onChange({ approveOnBehalfOfScope: val })}
              />
            )}
          </div>

          {/* Relationship manager — visible when either enabled toggle is Specific Users */}
          {showManager && (
            <RelationshipManager
              key={defaultTab}
              entryRelationships={settings.enterTimeForOthersRelationships}
              approvalRelationships={settings.approveOnBehalfOfRelationships}
              onEntryChange={(rows) => onChange({ enterTimeForOthersRelationships: rows })}
              onApprovalChange={(rows) => onChange({ approveOnBehalfOfRelationships: rows })}
              defaultTab={defaultTab}
              showEntry={enterSpecific}
              showApproval={approveSpecific}
              people={people}
            />
          )}

        </div>
      </div>
    </TabsContent>
  );
}
